import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from "@devhop/ui";
import {
  AlertTriangleIcon,
  ClockIcon,
  GraduationCapIcon,
  UsersIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import type { EligibleNotificationsPackageFilter } from "../lib/eligible-notifications-filters";

type PackageNotificationsSummaryCardsProps = {
  classCount: number;
  privateCount: number;
  pending: number;
  failed: number;
  isLoading?: boolean;
  activePackageFilter?: EligibleNotificationsPackageFilter;
  onPackageFilterChange?: (filter: EligibleNotificationsPackageFilter) => void;
  onQueuePendingClick?: () => void;
  onQueueFailedClick?: () => void;
};

function SummaryCard({
  title,
  value,
  icon,
  isLoading,
  accentClassName,
  active,
  onClick,
  subtitle,
}: {
  title: string;
  value: number;
  icon: ReactNode;
  isLoading?: boolean;
  accentClassName?: string;
  active?: boolean;
  onClick?: () => void;
  subtitle?: string;
}) {
  const content = (
    <>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="font-medium text-sm">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <span className="text-muted-foreground text-sm">…</span>
        ) : (
          <div className="font-bold text-2xl tabular-nums">
            {value.toLocaleString("lo-LA")}
          </div>
        )}
        {subtitle ? (
          <p className="mt-1 text-muted-foreground text-xs">{subtitle}</p>
        ) : null}
      </CardContent>
    </>
  );

  if (!onClick) {
    return (
      <Card className={cn("gap-0", accentClassName)}>{content}</Card>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        "text-left transition-colors",
        "rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      )}
      onClick={onClick}
    >
      <Card
        className={cn(
          "gap-0 transition-colors hover:bg-muted/30",
          accentClassName,
          active && "border-primary/50 bg-primary/5 ring-1 ring-primary/30",
        )}
      >
        {content}
      </Card>
    </button>
  );
}

export function PackageNotificationsSummaryCards({
  classCount,
  privateCount,
  pending,
  failed,
  isLoading,
  activePackageFilter = "all",
  onPackageFilterChange,
  onQueuePendingClick,
  onQueueFailedClick,
}: PackageNotificationsSummaryCardsProps) {
  const togglePackage = (type: EligibleNotificationsPackageFilter) => {
    if (!onPackageFilterChange) return;
    onPackageFilterChange(activePackageFilter === type ? "all" : type);
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        title="Eligible — ຄລາສ"
        value={classCount}
        isLoading={isLoading}
        icon={<GraduationCapIcon className="size-4 text-blue-600" />}
        accentClassName="border-blue-500/20 bg-blue-500/5"
        active={activePackageFilter === "package"}
        onClick={
          onPackageFilterChange ? () => togglePackage("package") : undefined
        }
        subtitle={
          onPackageFilterChange ? "ກົດເພື່ອກັ່ນຕາຕາລາງ" : undefined
        }
      />
      <SummaryCard
        title="Eligible — ສ່ວນຕົວ"
        value={privateCount}
        isLoading={isLoading}
        icon={<UsersIcon className="size-4 text-violet-600" />}
        accentClassName="border-violet-500/20 bg-violet-500/5"
        active={activePackageFilter === "private_lesson"}
        onClick={
          onPackageFilterChange
            ? () => togglePackage("private_lesson")
            : undefined
        }
        subtitle={
          onPackageFilterChange ? "ກົດເພື່ອກັ່ນຕາຕາລາງ" : undefined
        }
      />
      <SummaryCard
        title="ຄິວລໍຖ້າສົ່ງ"
        value={pending}
        isLoading={isLoading}
        icon={<ClockIcon className="size-4 text-amber-600" />}
        accentClassName="border-amber-500/20 bg-amber-500/5"
        onClick={onQueuePendingClick}
        subtitle={onQueuePendingClick ? "ເບິ່ງປະຫວັດການສົ່ງ" : undefined}
      />
      <SummaryCard
        title="ຄິວລົ້ມເຫຼວ"
        value={failed}
        isLoading={isLoading}
        icon={<AlertTriangleIcon className="size-4 text-destructive" />}
        accentClassName="border-destructive/20 bg-destructive/5"
        onClick={onQueueFailedClick}
        subtitle={onQueueFailedClick ? "ເບິ່ງປະຫວັດການສົ່ງ" : undefined}
      />
    </div>
  );
}
